import { Card, CardContent } from '@/modules/shared/components/ui/card'
import { useRef, useState } from 'react'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/modules/shared/components/ui/tabs'
import { useIsPwaInstalled } from '@/modules/shared/hooks/use-is-pwa-installed.hook'
import { cn } from '@/modules/shared/lib/utils'
import { toast } from 'sonner'
import type { useOrderForm } from '../../../hooks/use-order-form.hook'
import { useOrderFormCalculation } from '../../../hooks/use-order-form-calculation.hook'
import { useOrderFormStore } from '../../../stores/order-form.store'
import useOrderFormParams from '../../../hooks/use-order-form-params.hook'
import { useProductStore } from '../../../../product/stores/product.store'
import { HeaderComponent } from '../../header/header.component'
import { ORDER_FORM_TAB_ITEMS, ORDER_FORM_TAB_PANELS } from './order-tabs.constants'


export type OrderTabsComponentProps = ReturnType<typeof useOrderForm>

export function OrderTabsComponent({
	hasClientSelected,
	isViewOrder,
}: OrderTabsComponentProps) {
	const { calculateOrderForm } = useOrderFormCalculation()

	const { jsonOrderForm } = useOrderFormStore()
	const { getTabVisibility, hasValidPaymentTerms } = useOrderFormParams()
	const { actions: { clearProducts } } = useProductStore()
	const [activeTab, setActiveTab] = useState(ORDER_FORM_TAB_ITEMS[0].textTab)
	const tabsRef = useRef<HTMLDivElement>(null)

	const userAgent = navigator.userAgent
	const isIOS = !!userAgent.match(/iPhone|iPad|iPod/)
	const isPwaInstalled = useIsPwaInstalled()

	const isTabDisabled = (tabName: string) => {
		// Liberar a aba "Carrinho" quando estiver em modo de visualização
		if (isViewOrder && tabName === 'Carrinho') {
			return false
		}
		// Se a tab 'Prazos' não estiver visível, não valida condições de pagamento
		if (!getTabVisibility("Prazos")) {
			// Apenas valida se tem cliente selecionado
			if (!hasClientSelected || !jsonOrderForm?.cliente?.idEntidade) {
				return true
			}
			return false
		}

		if (!hasClientSelected || !jsonOrderForm?.cliente?.idEntidade) {
			return true
		}

		// Só valida prazos se a tab 'Prazos' estiver habilitada
		if (
			['Itens', 'Carrinho', 'Finalizar'].includes(tabName)
		) {
			return !hasValidPaymentTerms
		}
		return false
	}

	const handleTabClick = (tabName: string) => {
		if (tabName === activeTab) return

		if (isTabDisabled(tabName)) {
			if (!hasClientSelected || !jsonOrderForm?.cliente?.idEntidade) {
				toast.info('Selecione um cliente primeiro')
			} else if (['Itens', 'Carrinho', 'Finalizar'].includes(tabName) && !hasValidPaymentTerms && getTabVisibility("Prazos")) {
				toast.info('Preencha pelo menos um prazo de pagamento para continuar')
			}
			return
		}

		// O Radix troca o painel no mousedown, antes do blur nativo do campo.
		// Confirma a edição enquanto o editor ainda está montado.
		const focusedElement = document.activeElement
		if (focusedElement instanceof HTMLElement && tabsRef.current?.contains(focusedElement)) {
			focusedElement.blur()
		}

		setActiveTab(tabName)

		const baseUrl = window.location.href.split('?')[0]
		window.history.pushState(
			{
				path: baseUrl,
			},
			'',
			baseUrl,
		)

		clearProducts()

		const currentOrder = useOrderFormStore.getState()
		if (tabName === 'Itens' || currentOrder.isCalculationOrder) {
			calculateOrderForm(currentOrder.jsonOrderForm)
		}
	}

	return (
		<div className="space-y-2 min-h-screen pb-10">
			<Tabs ref={tabsRef} value={activeTab} onValueChange={handleTabClick}>
				<TabsList
					className={cn(
						'bg-primary flex justify-between text-sm h-20 text-white px-0 sm:pb-0 sm:h-14 fixed bottom-0 left-0 rounded-none z-10 w-full md:static md:flex md:bottom-auto md:left-auto md:rounded-none md:z-0',
						isIOS && isPwaInstalled ? 'h-20 pb-4 ' : 'h-16',
					)}
				>
					{ORDER_FORM_TAB_ITEMS.map((item) => {

						if (!getTabVisibility(item.textTab)) return
						return (
							<TabsTrigger
								disabled={isTabDisabled(item.textTab)}
								key={item.id}
								value={item.textTab}
								className={cn(
									'flex-1 data-[state=active]:bg-red-500/70 data-[state=active]:text-white h-14 md:py-0',
									item.textTab === 'Itens' && isViewOrder ? 'hidden' : null,
									isIOS && isPwaInstalled ? 'h-16' : 'h-14 mb-1',
								)}
							>
								<div className="items-center flex flex-col gap-2">
									<item.icon size={20} />
									<p className="text-xs">{item.textTab}</p>
								</div>
							</TabsTrigger>
						)
					})}
				</TabsList>
				{ORDER_FORM_TAB_PANELS.map((item) => {
					return (
						<TabsContent key={item.id} value={item.text}>
							<Card>
								<CardContent className="p-2">
									<item.component />
								</CardContent>
							</Card>
						</TabsContent>
					)
				})}
			</Tabs>
		</div>
	)
}
